// Packages and components
import Color from 'colorjs.io';

// Helpers and utilities
import { directionToDegrees } from './sizing';
import { ColorSettings, colors } from '@theme/globals/colors';

// Types
import type {
  ColorPalette,
  ColorType,
  LinearGradientDirection,
  RadialGradientDirection,
  RadialGradientShape,
} from '@theme/theme.d';

/**
 * Internal function for building a color palette from a name and a collection of colors.
 *
 * @param name -  The name of the palette (string or array of strings).
 * @param collection - An object containing colors for the palette.
 * @returns A color palette object with keys constructed from the name and collection keys.
 */
const buildPalette = (
  name: string | Array<string>,
  collection: ColorPalette,
  baseColor: ColorType
) => {
  const colorPalette: ColorPalette = {};
  const paletteNames = Array.isArray(name) ? name : [name];

  paletteNames.forEach((keyName: string) => {
    colorPalette[`${keyName}`] = getColor(baseColor);
    for (const [key, value] of Object.entries(collection)) {
      colorPalette[`${keyName}-${key}`] = value;
    }
  });

  return colorPalette;
};

/**
 * Internal function for adjusting a color's lightness and chroma.
 *
 * @param color - An array representing the color in LCH space ([lightness, chroma, hue]).
 * @param lightnessTuningFactor - A number to adjust the lightness value.
 * @param chromaTuningFactor - An optional number to adjust the chroma value (defaults to 0).
 * @returns A string representing the adjusted color converted to the specified space.
 */
const getAdjustedColor = (
  color: number[],
  lightnessTuningFactor: number,
  chromaTuningFactor = 0
): string => {
  const [l, c, h] = color;

  if (lightnessTuningFactor === 0) {
    return getColor(new Color('lch', [l, c, h]));
  }

  return getColor(
    new Color('lch', [l + lightnessTuningFactor, c + chromaTuningFactor, h])
  );
};

/**
 * Checks browser support for the OKLCH color format using CSS.supports.
 *
 * @export
 * @type {boolean}
 */
export const isOklchCompliant: boolean =
  CSS.supports && CSS.supports('color', 'oklch(69.9% 0.1203 182.23)');

/**
 * Converts a color to a string in the specified space with defined precision.
 *
 * @param color - A ColorType object or a Color object.
 * @returns A string representing the converted color.
 *
 * @export
 */
export const getColor = (color: ColorType | Color): string => {
  return new Color(color).to(ColorSettings.SPACE as string).toString({
    format: ColorSettings.SPACE as string,
    precision: ColorSettings.PRECISION as number,
  });
};

/**
 * Generates a weighted color range based on a provided LCH color.
 *
 * @param lch - A Color object representing the base color in LCH space.
 * @returns An object containing color strings for different weight levels (50-900).
 *
 * @export
 */
export const getWeightedColorRange = (color: Color) => {
  const { l, c, h } = color;

  const safeMaxLightness = l >= 88.5 ? 99.5 : ColorSettings.MAX_LIGHTNESS;
  const safeMinLightness = l <= 33 ? 0 : ColorSettings.MIN_LIGHTNESS;
  const lightBase = (safeMaxLightness - l) / 5;
  const darkBase = (-1 * (l - safeMinLightness)) / 8;
  return {
    50: getAdjustedColor([l, c, h], 5 * lightBase, -0.00375),
    100: getAdjustedColor([l, c, h], 4 * lightBase, -0.00375),
    200: getAdjustedColor([l, c, h], 3 * lightBase, -0.00375),
    300: getAdjustedColor([l, c, h], 2 * lightBase, -0.00375),
    400: getAdjustedColor([l, c, h], lightBase, -0.00375),
    500: getAdjustedColor([l, c, h], 0),
    600: getAdjustedColor([l, c, h], 1.6 * darkBase, 0.025),
    700: getAdjustedColor([l, c, h], 1.875 * 2 * darkBase, 0.05),
    800: getAdjustedColor([l, c, h], 3 * 2 * darkBase, 0.075),
    900: getAdjustedColor([l, c, h], 4 * 2 * darkBase, 0.1),
  };
};

const normalizeLch = (coordinate: number): number => {
  if (Number.isNaN(coordinate)) {
    return 0;
  }

  return parseFloat(coordinate.toFixed(ColorSettings.PRECISION));
};

/**
 * Generates a weighted color range based on a provided LCH color.
 *
 * @param lch - A Color object representing the base color in LCH space.
 * @returns An object containing color strings for different weight levels (50-900).
 *
 * @export
 */
export const getAlphaColorRange = (color: Color) => {
  const colorPalette: ColorPalette = {};
  const l = color.l * 100;
  const c = normalizeLch(color.c);
  const h = normalizeLch(color.h);

  for (let i = 5; i <= 95; i += 5) {
    colorPalette[`alpha-${i}`] = getColor(
      new Color(`oklch(${l}% ${c} ${h} / ${i}%)`)
    );
  }
  return colorPalette;
};

/**
 * Creates a color palette with a name and a base color.
 *
 * @param name - The name of the palette (string or array of strings).
 * @param baseColor - A ColorType object representing the base color.
 * @returns A color palette object containing weighted and alpha color variations.
 *
 * @export
 */
export const getColorPalette = (
  name: string | Array<string>,
  baseColor: ColorType,
  hasWeightedColors: boolean = false
): ColorPalette => {
  const color = new Color(baseColor);

  let combinedPalettes: { [key: string]: string } = {
    ...getAlphaColorRange(color.to('oklch')),
  };

  if (hasWeightedColors) {
    combinedPalettes = {
      ...combinedPalettes,
      ...getWeightedColorRange(color.to('lch')),
    };
  }

  return buildPalette(name, combinedPalettes, baseColor);
};

/**
 * Generates a linear gradient CSS string.
 *
 * @param {LinearGradientDirection} [direction='left'] - The direction of the gradient.
 * @param {string} color1 - The starting color of the gradient.
 * @param {string} color2 - The ending color of the gradient.
 * @returns {string} The linear gradient CSS string.
 */
export const getLinearGradient = (
  direction: LinearGradientDirection = 'left',
  color1: string,
  color2: string
) => {
  const range = Color.range(color1, color2);
  const stops = Color.steps(range, { steps: 10 });
  return `linear-gradient(${directionToDegrees(direction)}, ${stops.map((c) => c.to('oklch')).join(', ')})`;
};

/**
 * Generates a radial gradient CSS string.
 *
 * @param {RadialGradientShape} [shape='circle'] - The shape of the gradient.
 * @param {RadialGradientDirection} [direction='center'] - The direction of the gradient.
 * @param {string} color1 - The starting color of the gradient.
 * @param {string} color2 - The ending color of the gradient.
 * @returns {string} The radial gradient CSS string.
 */
export const getRadialGradient = (
  shape: RadialGradientShape = 'circle',
  direction: RadialGradientDirection = 'center',
  color1: string,
  color2: string
) => {
  const range = Color.range(color1, color2);
  const stops = Color.steps(range, { steps: 10 });
  return `radial-gradient(${shape} at ${direction} ${stops.map((c) => c.to('oklch')).join(', ')})`;
};

export const isLegalColor = (color: string): boolean => {
  if (colors[color]) {
    return true;
  }
  const testColor = new Color(color);
  return testColor.coords.every(
    (coord) => typeof coord === 'number' && !isNaN(coord)
  );
};
